import React, { useState, useRef, useEffect } from 'react';
import { uploadFile } from '../api';

export const CONDITION     = ['Good', 'Fair', 'Poor'];
export const CONDITION_CLR = { Good: 'text-emerald-600', Fair: 'text-amber-600', Poor: 'text-red-600' };
const CONDITION_HEX = { Good: '#059669', Fair: '#d97706', Poor: '#dc2626' };
export const TITLE_STATUS  = ['Clean', 'Salvage', 'Rebuilt', 'Lien', 'Other'];
export const GRADES        = ['A', 'B', 'C', 'D', 'F'];

const GRADE_CLR = {
  A: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800',
  B: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800',
  C: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800',
  D: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-800',
  F: 'bg-red-50 text-red-600 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800',
};

export function GradeBadge({ grade }) {
  const g = GRADES.includes(grade) ? grade : 'A';
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ${GRADE_CLR[g]}`}>
      Grade {g}
    </span>
  );
}
export const SALES_READY   = [
  { value: 'not_inspected', label: 'Not Inspected' },
  { value: 'sales_ready',   label: 'Sales Ready' },
];
export const SALE_CONDITION = [
  { value: 'as_is',  label: 'As Is' },
  { value: 'we_owe', label: 'We Owe' },
];
export const WARRANTY = [
  { value: 'without_warranty', label: 'Without Warranty' },
  { value: 'with_warranty',    label: 'With Warranty' },
];
export const CURRENT_YEAR  = new Date().getFullYear();
export const VIN_RE        = /^[A-HJ-NPR-Z0-9]{17}$/i;
export const URL_RE        = /^https?:\/\/.+/i;

const toTitleCase = s =>
  s.toLowerCase().replace(/(?:^|\s|-)\S/g, c => c.toUpperCase());

export function validateCar(car) {
  const e = {};
  if (!car.vin || !car.vin.trim())
    e.vin = 'VIN is required.';
  else if (!VIN_RE.test(car.vin.trim()))
    e.vin = 'VIN must be exactly 17 characters using letters A–Z (no I, O, Q) and digits 0–9.';
  if (!car.year && car.year !== 0) {
    e.year = 'Year is required.';
  } else {
    const y = parseInt(car.year, 10);
    if (isNaN(y) || y < 1900 || y > CURRENT_YEAR + 1)
      e.year = `Year must be between 1900 and ${CURRENT_YEAR + 1}.`;
  }
  if (!car.make || !car.make.trim())
    e.make = 'Make is required.';
  if (!car.model || !car.model.trim())
    e.model = 'Model is required.';
  if (!car.trim || !car.trim.trim())
    e.trim = 'Trim is required.';
  if (!car.mileage && car.mileage !== 0)
    e.mileage = 'Mileage is required.';
  else if (isNaN(car.mileage) || Number(car.mileage) < 0)
    e.mileage = 'Mileage must be 0 or greater.';
  if (!(car.bidding_enabled ?? true)) {
    if (!car.buy_now_price && car.buy_now_price !== 0)
      e.buy_now_price = 'Buy Now Price is required.';
    else if (isNaN(car.buy_now_price) || Number(car.buy_now_price) <= 0)
      e.buy_now_price = 'Must be a number greater than 0.';
  } else if (car.buy_now_price !== '' && car.buy_now_price != null &&
    (isNaN(car.buy_now_price) || Number(car.buy_now_price) <= 0)) {
    e.buy_now_price = 'Must be a number greater than 0.';
  }
  if (car.bidding_enabled ?? true) {
    if (!car.reserve_price && car.reserve_price !== 0)
      e.reserve_price = 'Reserve Price is required.';
    else if (isNaN(car.reserve_price) || Number(car.reserve_price) <= 0)
      e.reserve_price = 'Must be a number greater than 0.';
  }
  // Blank is allowed — the lot's default rate is applied server-side. Only a
  // value that is present must be a number in range.
  if (car.reserve_price !== '' && car.reserve_price != null &&
    car.buy_now_price !== '' && car.buy_now_price != null &&
    !isNaN(car.reserve_price) && !isNaN(car.buy_now_price) &&
    Number(car.reserve_price) > Number(car.buy_now_price)) {
    e.reserve_price = 'Reserve Price must not exceed Buy Now Price.';
  }
  if (car.tax_rate !== '' && car.tax_rate != null) {
    if (isNaN(car.tax_rate) || Number(car.tax_rate) < 0 || Number(car.tax_rate) > 100)
      e.tax_rate = 'Tax rate must be between 0 and 100.';
  }
  if (car.cost_basis !== '' && car.cost_basis != null) {
    if (isNaN(car.cost_basis) || Number(car.cost_basis) <= 0)
      e.cost_basis = 'Must be a number greater than 0.';
  }
  if (car.listing_url && car.listing_url.trim() && !URL_RE.test(car.listing_url.trim()))
    e.listing_url = 'Must be a valid URL starting with http:// or https://';
  if (!car.sales_ready)
    e.sales_ready = 'Please select an inspection status.';
  if (!car.sale_condition)
    e.sale_condition = 'Please select a sale condition.';
  else if (car.sale_condition === 'we_owe' && !(car.we_owe_details && car.we_owe_details.trim()))
    e.we_owe_details = 'Describe what you promise to fix.';
  if (!car.warranty)
    e.warranty = 'Please select a warranty status.';
  return e;
}

export function emptyCar(locationId = 1, defaults = {}) {
  return {
    _key: Math.random(),
    _needsAssociation: false,
    id: null,
    location_id: locationId,
    vin: '', year: '', make: '', model: '', trim: '', mileage: '',
    title_status: 'Clean', safety_exp: '', reg_exp: '',
    tires: 'Good', brakes: 'Good', ac: 'Good', engine: 'Good',
    transmission: 'Good', leaks: 'Good', doors_locks: 'Good',
    exhaust: 'Good', windshield: 'Good', tint: 'Good',
    warning_lights: '', obd_scan_notes: '',
    seller_disclosure: defaults.default_seller_disclosure || '',
    photos_videos: [], stock_number: '', reserve_price: '', buy_now_price: '',
    cost_basis: '',
    bidding_enabled: true, listing_url: '',
    tax_rate: defaults.default_tax_rate ?? 0, sales_ready: 'not_inspected',
    grade: 'A', operational: true,
    sale_condition: 'as_is', we_owe_details: '', warranty: 'without_warranty',
    warranty_document_url: '',
  };
}

export function fromInventory(car) {
  return {
    ...emptyCar(),
    ...car,
    _key: car.id,
    _needsAssociation: true,
    safety_exp: car.safety_exp?.slice(0, 7) || '',
    reg_exp: car.reg_exp?.slice(0, 7) || '',
    buy_now_price: car.buy_now_price ?? '',
    reserve_price: car.reserve_price ?? '',
    cost_basis: car.cost_basis ?? '',
    bidding_enabled: car.bidding_enabled ?? true,
    mileage: car.mileage ?? '',
    year: car.year ?? '',
    tax_rate: car.tax_rate ?? 0,
    sales_ready: car.sales_ready || 'not_inspected',
    grade: car.grade || 'A',
    operational: car.operational !== false,
    sale_condition: car.sale_condition || 'as_is',
    we_owe_details: car.we_owe_details || '',
    warranty: car.warranty || 'without_warranty',
    warranty_document_url: car.warranty_document_url || '',
  };
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="mt-1 text-xs text-red-600 flex items-start gap-1">
      <svg className="w-3 h-3 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {msg}
    </p>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="section-title">{title}</span>
        <div className="flex-1 h-px bg-slate-100" />
      </div>
      {children}
    </div>
  );
}

export function CarForm({ car, onChange, errors = {}, onUploadError, disabled = false }) {
  const inputCls = f => `input${errors[f] ? ' border-red-400 focus:ring-red-500' : ''}`;

  const [uploading, setUploading]       = useState(0);
  const [uploadingWarranty, setUploadingWarranty] = useState(false);
  const [decoding, setDecoding]         = useState(false);
  const [decodeStatus, setDecodeStatus] = useState(null); // 'success' | 'error' | null
  const [decodeMsg, setDecodeMsg]       = useState('');
  const fileInputRef    = useRef(null);
  const warrantyFileInputRef = useRef(null);
  const lastDecodedVin  = useRef('');
  const latestCar       = useRef(car);
  latestCar.current = car;

  const set = f => e => {
    onChange({ ...car, [f]: e.target.value });
  };

  // Auto-decode when a valid 17-char VIN is entered
  useEffect(() => {
    const vin = (car.vin || '').trim().toUpperCase();
    if (!VIN_RE.test(vin)) {
      if (decodeStatus) setDecodeStatus(null);
      return;
    }
    if (vin === lastDecodedVin.current) return;
    lastDecodedVin.current = vin;

    let cancelled = false;
    setDecoding(true);
    setDecodeStatus(null);

    fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vin}?format=json`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        // The VIN may have changed again while this request was in flight — a stale
        // response for an old VIN must not clobber fields for the VIN now in the field.
        if ((latestCar.current.vin || '').trim().toUpperCase() !== vin) return;
        const r = data.Results?.[0];
        if (!r || (!r.Make && !r.Model)) throw new Error('VIN not found in NHTSA database');

        // VIN decode is authoritative: it always clears/overwrites year/make/model/trim,
        // even values the user already typed, since a new VIN usually means a different car.
        const patch = { year: '', make: '', model: '', trim: '' };
        if (r.ModelYear && r.ModelYear !== '0') patch.year  = r.ModelYear;
        if (r.Make)                              patch.make  = toTitleCase(r.Make);
        if (r.Model)                             patch.model = toTitleCase(r.Model);
        if (r.Trim)                              patch.trim  = r.Trim;

        onChange({ ...latestCar.current, ...patch });
        setDecodeStatus('success');
      })
      .catch(e => {
        if (cancelled) return;
        setDecodeMsg(e.message || 'Could not decode VIN');
        setDecodeStatus('error');
      })
      .finally(() => { if (!cancelled) setDecoding(false); });

    return () => { cancelled = true; };
  }, [car.vin]); // eslint-disable-line

  const handleVinChange = e => {
    const vin = e.target.value.toUpperCase();
    onChange({ ...car, vin });
  };

  const photos = Array.isArray(car.photos_videos) ? car.photos_videos : [];

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';
    setUploading(u => u + files.length);
    const results = await Promise.allSettled(files.map(f => uploadFile(f)));
    const urls = results.filter(r => r.status === 'fulfilled').map(r => r.value.url);
    const failed = results.filter(r => r.status === 'rejected').length;
    if (urls.length) onChange({ ...car, photos_videos: [...photos, ...urls] });
    if (failed && onUploadError) onUploadError(`${failed} file${failed > 1 ? 's' : ''} failed to upload`);
    setUploading(u => u - files.length);
  };

  const removePhoto = (idx) =>
    onChange({ ...car, photos_videos: photos.filter((_, i) => i !== idx) });

  const handleWarrantyFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingWarranty(true);
    try {
      const { url } = await uploadFile(file);
      onChange({ ...car, warranty_document_url: url });
    } catch {
      if (onUploadError) onUploadError('Warranty document failed to upload');
    } finally {
      setUploadingWarranty(false);
    }
  };

  return (
    <fieldset disabled={disabled} className={`space-y-7 ${disabled ? 'opacity-60' : ''}`}>
      <Section title="Basic Info">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="col-span-2">
            <label className="label">VIN <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                className={inputCls('vin')}
                value={car.vin ?? ''}
                onChange={handleVinChange}
                placeholder="1HGCM82633A004352"
                maxLength={17}
                style={{ paddingRight: decoding || decodeStatus ? '2.25rem' : undefined }}
              />
              {decoding && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin inline-block" />
                </span>
              )}
              {!decoding && decodeStatus === 'success' && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-green-500" title="VIN decoded">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
              {!decoding && decodeStatus === 'error' && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-red-400" title={decodeMsg}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </span>
              )}
            </div>
            {!decoding && decodeStatus === 'success' && (
              <p className="mt-1 text-xs text-green-600 dark:text-green-400">Year, make, model and trim prefilled from VIN</p>
            )}
            {!decoding && decodeStatus === 'error' && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{decodeMsg} — fill fields manually</p>
            )}
            <FieldError msg={errors.vin} />
          </div>
          <div>
            <label className="label">Year <span className="text-red-500">*</span></label>
            <input className={inputCls('year')} type="number" min="0" step="1" value={car.year ?? ''} onChange={set('year')} placeholder="2021" />
            <FieldError msg={errors.year} />
          </div>
          <div>
            <label className="label">Make <span className="text-red-500">*</span></label>
            <input className={inputCls('make')} value={car.make ?? ''} onChange={set('make')} placeholder="Honda" />
            <FieldError msg={errors.make} />
          </div>
          <div>
            <label className="label">Model <span className="text-red-500">*</span></label>
            <input className={inputCls('model')} value={car.model ?? ''} onChange={set('model')} placeholder="Accord" />
            <FieldError msg={errors.model} />
          </div>
          <div>
            <label className="label">Trim <span className="text-red-500">*</span></label>
            <input className={inputCls('trim')} value={car.trim ?? ''} onChange={set('trim')} placeholder="EX-L" />
            <FieldError msg={errors.trim} />
          </div>
          <div>
            <label className="label">Mileage <span className="text-red-500">*</span></label>
            <input className={inputCls('mileage')} type="number" min="0" step="1" value={car.mileage ?? ''} onChange={set('mileage')} placeholder="45,000" />
            <FieldError msg={errors.mileage} />
          </div>
          <div>
            <label className="label">Stock #</label>
            <input className="input" value={car.stock_number ?? ''} onChange={set('stock_number')} placeholder="STK-001" />
          </div>
          <div className="col-span-2">
            <label className="label">Listing Link</label>
            <input
              className={inputCls('listing_url')}
              value={car.listing_url ?? ''}
              onChange={set('listing_url')}
              placeholder="https://yourdealership.com/inventory/this-car"
            />
            <FieldError msg={errors.listing_url} />
          </div>
        </div>
      </Section>

      <Section title="Pricing">
        <div className="mb-3 flex items-center gap-2">
          <input
            id={`bidding_enabled_${car._key}`}
            type="checkbox"
            checked={car.bidding_enabled ?? true}
            onChange={e => onChange({ ...car, bidding_enabled: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor={`bidding_enabled_${car._key}`} className="text-sm text-slate-600 dark:text-slate-400">
            Accept bids (in addition to Buy Now)
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Buy Now Price {!(car.bidding_enabled ?? true) && <span className="text-red-500">*</span>}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input className={`${inputCls('buy_now_price')} pl-7`} type="number" value={car.buy_now_price ?? ''} onChange={set('buy_now_price')} placeholder="18,500" />
            </div>
            <FieldError msg={errors.buy_now_price} />
          </div>
          {(car.bidding_enabled ?? true) && (
            <div>
              <label className="label">Reserve Price <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input className={`${inputCls('reserve_price')} pl-7`} type="number" value={car.reserve_price ?? ''} onChange={set('reserve_price')} placeholder="15,000" />
              </div>
              <FieldError msg={errors.reserve_price} />
            </div>
          )}
          <div>
            <label className="label">Tax Rate</label>
            <div className="relative">
              <input className={`${inputCls('tax_rate')} pr-7`} type="number" min="0" max="100" step="0.01"
                value={car.tax_rate ?? ''} onChange={set('tax_rate')} placeholder="0" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
            </div>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Defaults from this lot's settings — editable per car.</p>
            <FieldError msg={errors.tax_rate} />
          </div>
          <div>
            <label className="label">Cost Basis</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input className={`${inputCls('cost_basis')} pl-7`} type="number" value={car.cost_basis ?? ''} onChange={set('cost_basis')} placeholder="12,000" />
            </div>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">What you paid for this car. Private — never shown to buyers.</p>
            <FieldError msg={errors.cost_basis} />
          </div>
        </div>
      </Section>

      <Section title="Documentation">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Title Status</label>
            <select className="input" value={car.title_status || 'Clean'} onChange={set('title_status')}>
              {TITLE_STATUS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Safety Expiration</label>
            <input className="input" type="month" value={car.safety_exp || ''} onChange={set('safety_exp')} />
          </div>
          <div>
            <label className="label">Registration Expiration</label>
            <input className="input" type="month" value={car.reg_exp || ''} onChange={set('reg_exp')} />
          </div>
        </div>
      </Section>

      <Section title="Condition">
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="label">Inspection Status <span className="text-red-500">*</span></label>
            <select className={inputCls('sales_ready')} value={car.sales_ready || 'not_inspected'}
              onChange={set('sales_ready')}>
              {SALES_READY.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <FieldError msg={errors.sales_ready} />
          </div>
          <div>
            <label className="label">Grade <span className="text-red-500">*</span></label>
            <select className={inputCls('grade')} value={car.grade || 'A'} onChange={set('grade')}>
              {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <FieldError msg={errors.grade} />
          </div>
          <div>
            <label className="label">Operational Status</label>
            <button
              type="button"
              onClick={() => onChange({ ...car, operational: car.operational === false ? true : false })}
              className={`input text-left font-medium ${car.operational === false ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {car.operational === false ? 'Inoperable — tap to mark operational' : 'Operational — tap to flag inoperable'}
            </button>
          </div>
          <div>
            <label className="label">Sale Condition <span className="text-red-500">*</span></label>
            <select className={inputCls('sale_condition')} value={car.sale_condition || 'as_is'}
              onChange={set('sale_condition')}>
              {SALE_CONDITION.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <FieldError msg={errors.sale_condition} />
          </div>
          <div>
            <label className="label">Warranty <span className="text-red-500">*</span></label>
            <select className={inputCls('warranty')} value={car.warranty || 'without_warranty'}
              onChange={set('warranty')}>
              {WARRANTY.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <FieldError msg={errors.warranty} />
          </div>
          {car.warranty === 'with_warranty' && (
            <div className="col-span-2 sm:col-span-3">
              <label className="label">Warranty Document</label>
              <div className="flex items-center gap-3 flex-wrap">
                {car.warranty_document_url && (
                  <a
                    href={car.warranty_document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View current document
                  </a>
                )}
                <button
                  type="button"
                  disabled={uploadingWarranty}
                  onClick={() => warrantyFileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingWarranty ? 'Uploading…' : car.warranty_document_url ? 'Replace document' : 'Upload document'}
                </button>
                {car.warranty_document_url && !uploadingWarranty && (
                  <button
                    type="button"
                    onClick={() => onChange({ ...car, warranty_document_url: '' })}
                    className="text-sm text-red-500 hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={warrantyFileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleWarrantyFile}
                style={{ position: 'fixed', opacity: 0, width: 0, height: 0, left: 0, top: 0 }}
                tabIndex={-1}
                aria-hidden="true"
              />
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">PDF only — max 20 MB</p>
            </div>
          )}
          {car.sale_condition === 'we_owe' && (
            <div className="col-span-2 sm:col-span-3">
              <label className="label">What We Owe <span className="text-red-500">*</span></label>
              <textarea className={`${inputCls('we_owe_details')} resize-none`} rows={2}
                value={car.we_owe_details || ''} onChange={set('we_owe_details')}
                placeholder="New tires, fix check engine light…" />
              <FieldError msg={errors.we_owe_details} />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {['tires','brakes','ac','engine','transmission','leaks','doors_locks','exhaust','windshield','tint'].map(f => (
            <div key={f}>
              <label className="label">{f.replace('_', '/')}</label>
              <select className="input font-medium" style={{ color: CONDITION_HEX[car[f]] }}
                value={car[f] || 'Good'} onChange={e => onChange({ ...car, [f]: e.target.value })}>
                {CONDITION.map(o => <option key={o} value={o} style={{ color: CONDITION_HEX[o] }}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Notes">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div>
            <label className="label">Warning Lights</label>
            <input className="input" value={car.warning_lights || ''} onChange={set('warning_lights')} placeholder="None" />
          </div>
          <div>
            <label className="label">OBD Scan Notes</label>
            <textarea className="input resize-none" rows={3} value={car.obd_scan_notes || ''} onChange={set('obd_scan_notes')} placeholder="No codes found…" />
          </div>
          <div>
            <label className="label">Seller Disclosure</label>
            <textarea className="input resize-none" rows={3} value={car.seller_disclosure || ''} onChange={set('seller_disclosure')} placeholder="Prior accident, paint work…" />
          </div>
        </div>
      </Section>

      <Section title="Photos & Videos">
        <div className="space-y-3">
          {(photos.length > 0 || uploading > 0) && (
            <div className="flex flex-wrap gap-2">
              {photos.map((url, i) => (
                <div key={i} className="relative group w-24 h-20 shrink-0 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                  <img
                    src={url}
                    alt={`Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute inset-0 w-full h-full bg-black/0 hover:bg-black/30 transition-colors hidden group-hover:flex items-center justify-center"
                    aria-label="Remove photo"
                  >
                    <span className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs leading-none">✕</span>
                  </button>
                </div>
              ))}
              {Array.from({ length: uploading }).map((_, i) => (
                <div key={`u${i}`} className="w-24 h-20 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            disabled={uploading > 0}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {uploading > 0 ? `Uploading ${uploading}…` : 'Add photos / videos'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFiles}
            style={{ position: 'fixed', opacity: 0, width: 0, height: 0, left: 0, top: 0 }}
            tabIndex={-1}
            aria-hidden="true"
          />
          <p className="text-xs text-slate-400 dark:text-slate-500">JPEG, PNG, GIF, WebP, MP4, MOV — max 20 MB each</p>
        </div>
      </Section>
    </fieldset>
  );
}
